import { Component, ElementRef, OnInit, OnDestroy, ViewChild, AfterViewChecked, QueryList, ViewChildren } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { FirebaseTSFirestore, OrderBy } from 'firebasets/firebasetsFirestore/firebaseTSFirestore';
import { FirebaseTSAuth } from 'firebasets/firebasetsAuth/firebaseTSAuth';
import { FirebaseTSStorage } from 'firebasets/firebasetsStorage/firebaseTSStorage';

// Utiliser la même interface Message que dans le système de chat existant
export interface Message {
  id?: string; 
  senderId: string;
  receiverId?: string;
  text: string;
  timestamp: string;
  audioUrl?: string;
  imageUrl?: string;
  read?: boolean;
  readTimestamp?: string;
}

export interface AIMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  isTyping?: boolean;
  audioUrl?: string;
}

@Component({
  selector: 'app-niaga-ia',
  templateUrl: './niaga-ia.component.html',
  styleUrls: ['./niaga-ia.component.css']
})
export class NiagaIaComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messageContainer', { static: false }) private messageContainer: ElementRef;
  @ViewChild('messageInput') messageInput!: ElementRef;
  @ViewChildren('audioElement') audioPlayers: QueryList<ElementRef<HTMLAudioElement>>;

  messages: AIMessage[] = [];
  currentMessage: string = '';
  isLoading: boolean = false;
  isAITyping: boolean = false;
  niagaraAIImage: string = 'https://firebasestorage.googleapis.com/v0/b/lenali-21.appspot.com/o/profile2.png?alt=media&token=a24d9c4a-5daf-48c0-a265-88c4b0371f3c';

  // Propriétés audio
  currentAudioTime: { [key: string]: string } = {};
  totalDuration: { [key: string]: string } = {};
  currentProgress: { [key: string]: number } = {};
  audioErrors: Set<string> = new Set(); // Suivi des erreurs audio par ID de message

  // Propriétés pour l'enregistrement audio
  isRecordingAudio: boolean = false;
  mediaRecorder: MediaRecorder | null = null;
  audioChunks: Blob[] = [];
  recordingStartTime: number = 0;
  recordingTimer: any = null;
  recordingDurationText: string = '00:00';
  
  // Propriétés pour la progression d'enregistrement
  waveformBars: { height: number }[] = [];
  
  // Propriétés pour la prévisualisation
  recordedAudioBlob: Blob | null = null;
  previewAudioUrl: string = '';
  isPreviewPlaying: boolean = false;
  previewAudioElement: HTMLAudioElement | null = null;
  previewDurationText: string = '00:00';
  previewWaveformBars: number[] = [];

  // Propriétés Firebase
  private firestore = new FirebaseTSFirestore();
  private auth = new FirebaseTSAuth();
  private storage = new FirebaseTSStorage();
  private readonly NIAGA_AI_ID = 'XEPZczc1FIYsorIH0VXNBFRYZbl1';
  private chatId: string = '';
  private currentUserId: string = '';

  private shouldScrollToBottom = true;
  private isUserScrolling = false;
  private isNearBottom = true;

  private listeners: any[] = []; // Pour stocker les listeners actifs

  constructor(private http: HttpClient, private router: Router) { }

  ngOnInit(): void {
    // Initialiser l'utilisateur actuel et l'ID du chat
    const currentUser = this.auth.getAuth().currentUser;
    if (currentUser) {
      this.currentUserId = currentUser.uid;
      this.chatId = this.generateChatId(this.currentUserId, this.NIAGA_AI_ID);
      
      // Créer/initialiser le chat si nécessaire
      this.initializeChat();
      
      // Démarrer l'écoute en temps réel des messages
      this.startRealtimeMessageListener();
    } else {
      // Rediriger vers la connexion si pas d'utilisateur
      this.router.navigate(['/']);
    }
  }

  ngAfterViewChecked() {
    if (this.shouldScrollToBottom && this.isNearBottom && !this.isUserScrolling) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    // Le listener se nettoiera automatiquement lors de la destruction du composant
    console.log("Composant Niaga IA détruit");
  }

  private initializeChat(): void {
    // Vérifier si le chat existe, sinon le créer
    this.firestore.getDocument({
      path: ["ChatWithAi", this.chatId],
      onComplete: (result) => {
        if (!result.exists) {
          // Créer le chat avec la structure standard
          this.firestore.create({
            path: ["ChatWithAi", this.chatId],
            data: {
              id: this.chatId,
              participants: [this.currentUserId, this.NIAGA_AI_ID]
            },
            onComplete: () => {
              console.log("Chat avec Niaga IA créé");
            },
            onFail: (err) => {
              console.error("Erreur lors de la création du chat:", err);
            }
          });
        }
      },
      onFail: (err) => {
        console.error("Erreur lors de la vérification du chat:", err);
      }
    });
  }

  private startRealtimeMessageListener(): void {
    // Écouter les messages en temps réel avec FirebaseTS
    try {
      this.firestore.listenToCollection({
        name: "niagaIAMessagesListener",
        path: ["ChatWithAi", this.chatId, "messages"],
        where: [
          new OrderBy("timestamp", "asc")
        ],
        onUpdate: (result) => {
          console.log("Nouveaux messages reçus:", result.docs.length);
          this.processRealtimeMessages(result);
        }
      });
    } catch (error) {
      console.error("Erreur lors de la création du listener:", error);
      // Fallback : recharger manuellement
      this.loadConversationHistory();
    }
  }

  private processRealtimeMessages(result: any): void {
    const newMessages: AIMessage[] = [];
    
    result.docs.forEach((doc: any) => {
      const data = doc.data() as Message;
      
      console.log('=== MESSAGE REÇU DE FIREBASE ===');
      console.log('Doc ID:', doc.id);
      console.log('Data complète:', data);
      console.log('audioUrl dans data:', data.audioUrl);
      console.log('Type de audioUrl:', typeof data.audioUrl);
      
      // Gestion plus robuste des timestamps
      let messageTimestamp: Date;
      if (data.timestamp) {
        // Si c'est une string ISO, la convertir
        if (typeof data.timestamp === 'string') {
          messageTimestamp = new Date(data.timestamp);
        }
        // Si c'est un timestamp Firestore avec toDate()
        else if (typeof data.timestamp === 'object' && data.timestamp !== null && 'toDate' in data.timestamp) {
          messageTimestamp = (data.timestamp as any).toDate();
        }
        // Fallback pour autres cas
        else {
          messageTimestamp = new Date(data.timestamp as any);
        }
      } else {
        messageTimestamp = new Date();
      }

      // Vérifier si la date est valide
      if (isNaN(messageTimestamp.getTime())) {
        messageTimestamp = new Date();
      }

      const message: AIMessage = {
        id: data.id || doc.id,
        text: data.text || '',
        isUser: data.senderId === this.currentUserId,
        timestamp: messageTimestamp,
        audioUrl: data.audioUrl || ''
      };
      
      console.log('Message transformé:', message);
      console.log('audioUrl final:', message.audioUrl);
      
      newMessages.push(message);
    });

    // Trier par timestamp
    newMessages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    // Mettre à jour la liste des messages seulement si elle a changé
    if (this.hasMessagesChanged(newMessages)) {
      console.log('=== MISE À JOUR DES MESSAGES ===');
      console.log('Nombre de messages:', newMessages.length);
      console.log('Messages avec audio:', newMessages.filter(m => m.audioUrl && m.audioUrl !== '').length);
      
      this.messages = newMessages;
      this.shouldScrollToBottom = true;
      
      // Si un nouveau message arrive et que ce n'est pas nous qui l'avons envoyé
      if (newMessages.length > 0) {
        const lastMessage = newMessages[newMessages.length - 1];
        if (!lastMessage.isUser && !lastMessage.isTyping) {
          // Nouveaux message de l'IA ou depuis mobile, forcer le scroll
          this.isNearBottom = true;
          this.isUserScrolling = false;
          this.shouldScrollToBottom = true;
        }
      }
    }
  }

  private hasMessagesChanged(newMessages: AIMessage[]): boolean {
    if (newMessages.length !== this.messages.length) {
      return true;
    }
    
    // Comparer les IDs et timestamps pour détecter les changements
    for (let i = 0; i < newMessages.length; i++) {
      const newMsg = newMessages[i];
      const currentMsg = this.messages[i];
      
      if (!currentMsg || 
          newMsg.id !== currentMsg.id || 
          newMsg.text !== currentMsg.text ||
          newMsg.audioUrl !== currentMsg.audioUrl) {
        return true;
      }
    }
    
    return false;
  }

  loadConversationHistory(): void {
    // Cette méthode est maintenant un fallback en cas d'erreur du listener temps réel
    this.firestore.getCollection({
      path: ["ChatWithAi", this.chatId, "messages"],
      where: [
        new OrderBy("timestamp", "asc")
      ],
      onComplete: (result) => {
        this.processRealtimeMessages(result);
      },
      onFail: (err) => {
        console.error("Erreur lors du chargement des messages:", err);
        this.messages = [];
      }
    });
  }

  async sendMessage(): Promise<void> {
    if (!this.currentMessage.trim() || this.isLoading) return;

    const messageText = this.currentMessage.trim();
    this.currentMessage = '';

    // Ajouter le message utilisateur
    const userMessage: AIMessage = {
      id: this.generateMessageId(),
      text: messageText,
      isUser: true,
      timestamp: new Date()
    };
    this.messages.push(userMessage);

    // Sauvegarder le message utilisateur dans Firebase
    await this.saveMessageToFirebase(messageText, true);

    // Forcer le scroll vers le bas après l'envoi d'un message
    this.isNearBottom = true;
    this.isUserScrolling = false;
    this.shouldScrollToBottom = true;

    // Réinitialiser la hauteur du textarea
    if (this.messageInput) {
      this.messageInput.nativeElement.style.height = 'auto';
    }

    // DÉSACTIVÉ : Génération automatique de réponse IA
    // this.generateAIResponse(messageText);
  }

  sendSuggestedMessage(message: string): void {
    this.currentMessage = message;
    this.sendMessage();
  }

  async generateAIResponse(prompt: string): Promise<void> {
    this.isLoading = true;
    this.isAITyping = true;

    // Ajouter un indicateur de frappe temporaire
    const typingMessage: AIMessage = {
      id: 'typing',
      text: '',
      isUser: false,
      timestamp: new Date(),
      isTyping: true
    };
    this.messages.push(typingMessage);
    this.shouldScrollToBottom = true;

    // Simuler un délai d'API
    setTimeout(async () => {
      // Supprimer l'indicateur de frappe
      this.messages = this.messages.filter(m => !m.isTyping);

      const response = this.generateResponse(prompt);
      const aiMessage: AIMessage = {
        id: this.generateMessageId(),
        text: response,
        isUser: false,
        timestamp: new Date()
      };

      this.messages.push(aiMessage);
      
      // Sauvegarder la réponse IA dans Firebase
      await this.saveMessageToFirebase(response, false);

      this.isLoading = false;
      this.isAITyping = false;
      this.shouldScrollToBottom = true;
    }, 2000);
  }

  private generateMessageId(): string {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

  private generateResponse(userMessage: string): string {
    // Ici vous intégrerez votre vraie API IA (ChatGPT, Claude, etc.)
    // Pour l'instant, on simule des réponses
    const lowerMessage = userMessage.toLowerCase();
    
    if (lowerMessage.includes('publication') || lowerMessage.includes('post')) {
      return `Excellente question ! Pour améliorer vos publications sur Lenali, voici mes conseils :

• **Contenu authentique** : Partagez des expériences personnelles et authentiques
• **Visuels de qualité** : Utilisez des images nettes et bien éclairées
• **Timing optimal** : Publiez quand votre audience est la plus active
• **Engagement** : Répondez aux commentaires pour créer une communauté
• **Hashtags pertinents** : Utilisez des hashtags en rapport avec votre contenu
• **Photos sonores** : Exploitez cette fonctionnalité unique de Lenali pour vous démarquer

Avez-vous un type de contenu spécifique en tête ?`;
    }
    
    if (lowerMessage.includes('lenali') || lowerMessage.includes('pratique')) {
      return `Voici les meilleures pratiques pour utiliser Lenali efficacement :

• **Photos sonores** : C'est la spécialité de Lenali ! Ajoutez de l'audio à vos images pour créer une expérience immersive unique.

📱 **Optimisation mobile** : Lenali est conçu pour le mobile, assurez-vous que votre contenu s'affiche bien sur tous les écrans.

🤝 **Communauté** : Interagissez avec les autres utilisateurs, commentez et partagez du contenu de qualité.

🎯 **Authenticité** : Soyez vous-même, les utilisateurs apprécient le contenu genuine.

Qu'aimeriez-vous savoir de plus spécifique ?`;
    }

    if (lowerMessage.includes('aide') && lowerMessage.includes('rédiger')) {
      return `Je serais ravi de vous aider à rédiger un post captivant ! Voici ma méthode :

✍️ **Structure efficace** :
1. **Accroche** : Commencez par une question ou une phrase intrigante
2. **Corps** : Développez votre idée avec des détails concrets
3. **Appel à l'action** : Terminez en invitant à l'interaction

💡 **Conseils de rédaction** :
• Utilisez des émojis avec parcimonie mais efficacement
• Posez des questions pour encourager les commentaires
• Racontez une histoire plutôt que de simplement informer
• Adaptez le ton à votre audience

Quel est le sujet de votre prochain post ? Je peux vous aider à le structurer !`;
    }

    // Réponse par défaut
    return `Je comprends votre question ! En tant qu'assistant IA spécialisé dans Lenali, je peux vous aider avec :

• La création de contenu engageant
• L'optimisation de vos publications
• Les meilleures pratiques de la plateforme
• La gestion de votre profil
• L'utilisation des photos sonores
• Les stratégies d'engagement

Pouvez-vous être plus spécifique sur ce qui vous intéresse ? Je serai ravi de vous donner des conseils personnalisés !`;
  }

  clearConversation(): void {
    // Optionnel: Supprimer les messages de Firestore
    // Pour l'instant, on ne supprime que de l'interface locale
    this.messages = [];
    this.currentMessage = '';
    this.isLoading = false;
    this.isAITyping = false;
    
    // Si vous voulez vraiment supprimer de Firestore, décommentez ci-dessous:
    // this.clearFirestoreMessages();
  }

  private clearFirestoreMessages(): void {
    // Cette méthode supprime tous les messages du chat actuel de Firestore
    this.firestore.getCollection({
      path: ["ChatWithAi", this.chatId, "messages"],
      where: [], // Paramètre requis même s'il est vide
      onComplete: (result) => {
        const promises: Promise<void>[] = [];
        result.docs.forEach(doc => {
          promises.push(
            new Promise((resolve, reject) => {
              this.firestore.delete({
                path: ["ChatWithAi", this.chatId, "messages", doc.id],
                onComplete: () => resolve(),
                onFail: (err) => reject(err)
              });
            })
          );
        });
        
        Promise.all(promises).then(() => {
          console.log("Tous les messages du chat ont été supprimés de Firestore");
        }).catch((err) => {
          console.error("Erreur lors de la suppression des messages:", err);
        });
      },
      onFail: (err) => {
        console.error("Erreur lors de la récupération des messages à supprimer:", err);
      }
    });
  }

  formatMessage(text: string): string {
    // Convertir les retours à la ligne en <br>
    let formatted = text.replace(/\n/g, '<br>');
    
    // Convertir les puces • en puces HTML
    formatted = formatted.replace(/•/g, '<span class="bullet">•</span>');
    
    // Mettre en gras le texte entre **
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Traiter les émojis et icônes
    formatted = formatted.replace(/🌟|📱|🤝|🎯|✍️|💡/g, '<span class="emoji">$&</span>');
    
    return formatted;
  }

  getFormattedTime(timestamp: Date): string {
    const messageDate = timestamp;
    const now = new Date();

    // Calcul de la différence en millisecondes
    const diffInMs = now.getTime() - messageDate.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60); // conversion en heures
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24); // conversion en jours

    // Si le message date d'aujourd'hui
    if (diffInHours < 24) {
      return messageDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false });
    } 

    // Si le message date d'hier
    else if (diffInHours >= 24 && diffInHours < 48) {
      return `Hier, ${messageDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
    }

    // Si le message est vieux de plus de 48 heures mais dans la même année
    else if (diffInDays >= 2 && now.getFullYear() === messageDate.getFullYear()) {
      return `${messageDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} à ${messageDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
    }

    // Si le message date de plus d'une année
    else {
      return messageDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) + ' à ' +
             messageDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false });
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  adjustTextareaHeight(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  private scrollToBottom(): void {
    try {
      if (this.messageContainer) {
        this.messageContainer.nativeElement.scrollTop = this.messageContainer.nativeElement.scrollHeight;
        this.isNearBottom = true;
      }
    } catch(err) {
      console.error('Erreur lors du scroll:', err);
    }
  }

  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const threshold = 100; // Seuil en pixels pour considérer qu'on est "en bas"
    
    // Calculer si l'utilisateur est près du bas
    const isAtBottom = element.scrollHeight - element.scrollTop - element.clientHeight <= threshold;
    
    // Si l'utilisateur scroll et n'est plus en bas, on considère qu'il navigue l'historique
    if (!isAtBottom) {
      this.isUserScrolling = true;
      this.isNearBottom = false;
    } else {
      this.isUserScrolling = false;
      this.isNearBottom = true;
    }
  }

  goBack(): void {
    this.router.navigate(['/postfeed/feed']);
  }

  // === MÉTHODES DE GESTION AUDIO ===

  playAudio(audioElement: HTMLAudioElement | HTMLVideoElement) {
    console.log('=== TENTATIVE DE LECTURE AUDIO ===');
    console.log('Élément audio:', audioElement);
    console.log('Source URL:', audioElement?.src);
    
    // Vérifier si l'URL audio est valide
    if (!audioElement || !audioElement.src || audioElement.src === '' || audioElement.src === 'null') {
      console.warn('URL audio invalide ou vide');
      console.warn('audioElement:', audioElement);
      console.warn('audioElement.src:', audioElement?.src);
      return;
    }

    console.log('✅ URL audio valide, procédure de lecture...');

    // Si l'audio n'est pas encore chargé, le charger d'abord
    if (audioElement.readyState === 0) {
      audioElement.load();
      // Attendre que l'audio soit chargé avant de jouer
      audioElement.addEventListener('canplay', () => {
        this.performPlayAudio(audioElement);
      }, { once: true });
      return;
    }

    this.performPlayAudio(audioElement);
  }

  private performPlayAudio(audioElement: HTMLAudioElement | HTMLVideoElement) {
    const audioId = audioElement.src;
    
    // Arrêter tous les autres lecteurs audio et video
    this.audioPlayers?.forEach(player => {
      if (player.nativeElement !== audioElement) {
        player.nativeElement.pause();
        player.nativeElement.currentTime = 0;
        delete this.currentAudioTime[player.nativeElement.src];
        this.currentProgress[player.nativeElement.src] = 0;
      }
    });

    // Jouer ou mettre en pause l'audio sélectionné
    if (audioElement.paused) {
      audioElement.play().catch(error => {
        console.error('Erreur lors de la lecture audio:', error);
        // Essayer de recharger et rejouer
        audioElement.load();
        setTimeout(() => {
          audioElement.play().catch(e => {
            console.error('Échec de la seconde tentative de lecture:', e);
          });
        }, 500);
      });
    } else {
      audioElement.pause();
      audioElement.currentTime = 0;
      delete this.currentAudioTime[audioId];
      this.currentProgress[audioId] = 0;
    }
  }

  onTimeUpdate(event: Event) {
    const audioElement = event.target as HTMLAudioElement;
    const audioId = audioElement.src;
    if (!audioElement.paused && audioId) {
      this.currentProgress[audioId] = audioElement.currentTime;
      this.currentAudioTime[audioId] = this.formatTime(audioElement.currentTime);
    }
  }

  onAudioLoaded(event: Event) {
    const audioElement = event.target as HTMLAudioElement;
    const audioId = audioElement.src;
    console.log('=== AUDIO CHARGÉ AVEC SUCCÈS ===');
    console.log('URL:', audioId);
    console.log('Durée:', audioElement.duration);
    console.log('Format supporté:', audioElement.canPlayType);
    console.log('ReadyState:', audioElement.readyState);
    
    if (audioElement && audioId && isFinite(audioElement.duration)) {
      this.totalDuration[audioId] = this.formatTime(audioElement.duration);
      this.currentProgress[audioId] = 0;
      console.log('✅ Durée formatée:', this.totalDuration[audioId]);
    }
  }

  onAudioError(event: Event, messageId?: string) {
    const audioElement = event.target as HTMLAudioElement;
    console.error('=== ERREUR AUDIO DÉTAILLÉE ===');
    console.error('URL:', audioElement.src);
    console.error('Code d\'erreur:', audioElement.error?.code);
    console.error('Message d\'erreur:', audioElement.error?.message);
    console.error('ReadyState:', audioElement.readyState);
    console.error('NetworkState:', audioElement.networkState);
    
    // Analyser l'URL pour plus d'infos
    console.error('URL complète analysée:');
    console.error('- Contient 3gp/3gpp:', this.is3GPPFormat(audioElement.src));
    console.error('- Contient token:', audioElement.src.includes('token='));
    console.error('- Domaine:', new URL(audioElement.src).hostname);
    
    // Vérifier si c'est un problème de format 3GPP
    const is3GPP = this.is3GPPFormat(audioElement.src);
    
    if (is3GPP && audioElement.error?.code === 4) {
      console.warn('Format 3GPP détecté - Tentative avec élément video');
      this.try3GPPWithVideo(audioElement, messageId);
      return;
    }
    
    // Vérifier si c'est un problème de CORS ou de réseau
    if (audioElement.error?.code === 2) {
      console.error('Erreur réseau détectée - Tentative de rechargement');
      setTimeout(() => {
        audioElement.load();
      }, 1000);
      return;
    }
    
    // Marquer ce message comme ayant une erreur audio
    if (messageId) {
      this.audioErrors.add(messageId);
      console.error(`Message ${messageId} marqué comme erreur audio`);
    }
    
    // Nettoyer les données audio pour cet élément
    if (audioElement.src) {
      delete this.currentAudioTime[audioElement.src];
      delete this.totalDuration[audioElement.src];
      delete this.currentProgress[audioElement.src];
    }
  }

  private is3GPPFormat(url: string): boolean {
    if (!url) return false;
    
    // Vérifier les extensions et patterns typiques du 3GPP
    const url3GPPPatterns = [
      '.3gp', '.3gpp', '.3g2',
      '3gp', '3gpp', '3g2',
      'video%2F3gpp', 'video/3gpp',
      'audio%2F3gpp', 'audio/3gpp'
    ];
    
    const lowerUrl = url.toLowerCase();
    const found = url3GPPPatterns.some(pattern => lowerUrl.includes(pattern.toLowerCase()));
    
    if (found) {
      console.log('🎵 Format 3GPP détecté dans URL:', url);
    }
    
    return found;
  }

  private try3GPPWithVideo(audioElement: HTMLAudioElement, messageId?: string) {
    console.log('=== TENTATIVE 3GPP AVEC VIDEO ===');
    // Créer un élément video temporaire pour tester la lecture 3GPP
    const videoElement = document.createElement('video');
    videoElement.src = audioElement.src;
    videoElement.muted = true; // Pour éviter les problèmes d'autoplay
    
    videoElement.addEventListener('loadedmetadata', () => {
      console.log('✅ 3GPP chargé avec succès via élément video');
      console.log('Durée:', videoElement.duration);
      console.log('ReadyState:', videoElement.readyState);
      // Si ça marche avec video, on peut l'utiliser pour l'audio
      if (messageId && this.audioErrors.has(messageId)) {
        this.audioErrors.delete(messageId);
        console.log(`✅ Message ${messageId} retiré des erreurs`);
      }
    });
    
    videoElement.addEventListener('error', (e) => {
      console.error('❌ Échec du chargement 3GPP même avec élément video');
      console.error('Erreur video:', videoElement.error);
      if (messageId) {
        this.audioErrors.add(messageId);
        console.error(`❌ Message ${messageId} marqué comme erreur définitive`);
      }
    });
    
    videoElement.addEventListener('canplay', () => {
      console.log('✅ Video 3GPP peut être lue');
    });
    
    console.log('Chargement du video test...');
    videoElement.load();
  }

  onAudioCanPlay(event: Event) {
    const audioElement = event.target as HTMLAudioElement;
    console.log('=== AUDIO PEUT ÊTRE LU ===');
    console.log('URL:', audioElement.src);
    console.log('ReadyState:', audioElement.readyState);
    console.log('NetworkState:', audioElement.networkState);
    
    // Si l'audio peut être lu, retirer l'erreur si elle existait
    const message = this.messages.find(m => m.audioUrl === audioElement.src);
    if (message && this.audioErrors.has(message.id)) {
      this.audioErrors.delete(message.id);
    }
  }

  isAudioError(messageId: string): boolean {
    return this.audioErrors.has(messageId);
  }

  updateAudioTime(event: Event, audioElement: HTMLAudioElement | HTMLVideoElement) {
    const slider = event.target as HTMLInputElement;
    if (audioElement) {
      const newTime = Number(slider.value);
      audioElement.currentTime = newTime;
      this.currentProgress[audioElement.src] = newTime;
      this.currentAudioTime[audioElement.src] = this.formatTime(newTime);
    }
  }

  getCurrentProgress(audioElement: HTMLAudioElement | HTMLVideoElement | undefined): number {
    return audioElement ? this.currentProgress[audioElement.src] || 0 : 0;
  }

  getMaxDuration(audioElement: HTMLAudioElement | HTMLVideoElement | undefined): number {
    return audioElement && isFinite(audioElement.duration) ? audioElement.duration : 0;
  }

  formatTime(time: number): string {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  getAudioDuration(audioElement: HTMLAudioElement | HTMLVideoElement | undefined): string {
    if (!audioElement) return '00:00';
    
    const audioId = audioElement.src;
    
    // Si on a la durée totale et le temps actuel
    if (this.totalDuration[audioId] && this.currentAudioTime[audioId]) {
      return `${this.currentAudioTime[audioId]} / ${this.totalDuration[audioId]}`;
    }
    
    // Si on a seulement la durée totale
    if (this.totalDuration[audioId]) {
      return `00:00 / ${this.totalDuration[audioId]}`;
    }
    
    // Fallback
    return '00:00';
  }

  private generateChatId(userId: string, aiId: string): string {
    // Combiner les IDs de manière déterministe (toujours le même ordre)
    return userId < aiId ? `${userId}_${aiId}` : `${aiId}_${userId}`;
  }

  private async saveMessageToFirebase(messageText: string, isUser: boolean, audioUrl?: string): Promise<void> {
    try {
      if (!this.chatId || !this.currentUserId) return;

      const messageId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
      
      console.log('=== SAUVEGARDE MESSAGE FIREBASE ===');
      console.log('messageText:', messageText);
      console.log('isUser:', isUser);
      console.log('audioUrl reçu:', audioUrl);
      console.log('type audioUrl:', typeof audioUrl);
      
      const message: Message = {
        id: messageId,
        senderId: isUser ? this.currentUserId : this.NIAGA_AI_ID,
        receiverId: isUser ? this.NIAGA_AI_ID : this.currentUserId,
        text: messageText,
        timestamp: new Date().toISOString(),
        audioUrl: audioUrl || '',
        imageUrl: '',
        read: false,
        readTimestamp: ''
      };

      console.log('Message à sauvegarder:', message);
      console.log('audioUrl dans message:', message.audioUrl);

      await this.firestore.create({
        path: ["ChatWithAi", this.chatId, "messages", messageId],
        data: message,
        onComplete: () => {
          console.log('✅ Message sauvegardé avec succès dans Firebase');
        },
        onFail: (error) => {
          console.error('❌ Erreur lors de la sauvegarde:', error);
        }
      });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du message:', error);
    }
  }

  getAudioElement(audioElement: HTMLAudioElement | undefined, videoElement: HTMLVideoElement | undefined): HTMLAudioElement | HTMLVideoElement | undefined {
    // Retourner l'élément approprié selon le format
    if (videoElement && videoElement.src) {
      return videoElement;
    }
    return audioElement;
  }

  getAudioElementById(messageId: string): HTMLAudioElement | HTMLVideoElement | undefined {
    console.log('=== RECHERCHE ÉLÉMENT AUDIO ===');
    console.log('Message ID:', messageId);
    
    // Chercher d'abord l'élément audio
    const audioElement = document.getElementById('audio-' + messageId) as HTMLAudioElement;
    console.log('Audio element trouvé:', audioElement);
    console.log('Audio src:', audioElement?.src);
    
    if (audioElement && audioElement.src) {
      console.log('✅ Retour élément audio');
      return audioElement;
    }
    
    // Sinon chercher l'élément video (pour 3GPP)
    const videoElement = document.getElementById('video-' + messageId) as HTMLVideoElement;
    console.log('Video element trouvé:', videoElement);
    console.log('Video src:', videoElement?.src);
    
    if (videoElement && videoElement.src) {
      console.log('✅ Retour élément video');
      return videoElement;
    }
    
    console.warn('❌ Aucun élément audio/video trouvé pour ID:', messageId);
    return undefined;
  }

  playAudioById(messageId: string): void {
    console.log('=== LECTURE AUDIO PAR ID ===');
    console.log('Message ID:', messageId);
    
    const audioElement = this.getAudioElementById(messageId);
    console.log('Élément trouvé:', audioElement);
    
    if (audioElement) {
      this.playAudio(audioElement);
    } else {
      console.error('❌ Impossible de trouver l\'élément audio pour le message:', messageId);
    }
  }

  // === MÉTHODES D'ENREGISTREMENT AUDIO ===

  handleButtonClick(): void {
    if (this.currentMessage.trim()) {
      // Envoi de message texte
      this.sendMessage();
    } else if (this.recordedAudioBlob) {
      // Envoi de l'audio prévisualisé
      this.sendRecordedAudio();
    } else if (this.isRecordingAudio) {
      // Arrêt de l'enregistrement
      this.stopAudioRecording();
    } else {
      // Démarrage de l'enregistrement
      this.startAudioRecording();
    }
  }

  async toggleAudioRecording(): Promise<void> {
    if (this.isRecordingAudio) {
      this.stopAudioRecording();
    } else {
      await this.startAudioRecording();
    }
  }

  async startAudioRecording(): Promise<void> {
    try {
      console.log('🎤 Demande de permission microphone...');
      
      // Réinitialiser les states
      this.recordedAudioBlob = null;
      this.previewAudioUrl = '';
      this.recordingDurationText = '00:00';
      
      // Demander l'accès au microphone
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      console.log('✅ Permission microphone accordée');
      
      // Créer le MediaRecorder
      const options = { mimeType: 'audio/webm;codecs=opus' };
      
      // Vérifier la compatibilité du format
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        console.warn('Format opus non supporté, utilisation du format par défaut');
        this.mediaRecorder = new MediaRecorder(stream);
      } else {
        this.mediaRecorder = new MediaRecorder(stream, options);
      }
      
      this.audioChunks = [];
      
      // Événements du MediaRecorder
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };
      
      this.mediaRecorder.onstop = () => {
        console.log('🎤 Enregistrement terminé');
        this.processRecordedAudio();
        
        // Arrêter le stream
        stream.getTracks().forEach(track => track.stop());
      };
      
      this.mediaRecorder.onerror = (event) => {
        console.error('Erreur MediaRecorder:', event);
        this.isRecordingAudio = false;
        stream.getTracks().forEach(track => track.stop());
      };
      
      // Démarrer l'enregistrement
      this.mediaRecorder.start();
      this.isRecordingAudio = true;
      this.recordingStartTime = Date.now();
      
      // Démarrer le timer de durée
      this.startRecordingTimer();
      
      console.log('🎤 Enregistrement démarré');
      
    } catch (error) {
      console.error('Erreur accès microphone:', error);
      alert('Impossible d\'accéder au microphone. Veuillez autoriser l\'accès dans votre navigateur.');
    }
  }

  stopAudioRecording(): void {
    if (this.mediaRecorder && this.isRecordingAudio) {
      console.log('🛑 Arrêt de l\'enregistrement...');
      this.mediaRecorder.stop();
      this.isRecordingAudio = false;
      
      if (this.recordingTimer) {
        clearInterval(this.recordingTimer);
        this.recordingTimer = null;
      }
    }
  }

  private startRecordingTimer(): void {
    this.recordingTimer = setInterval(() => {
      const elapsed = Date.now() - this.recordingStartTime;
      const seconds = Math.floor(elapsed / 1000);
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      
      this.recordingDurationText = `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    }, 100);
  }

  private async processRecordedAudio(): Promise<void> {
    if (this.audioChunks.length === 0) {
      console.warn('Aucun données audio enregistrées');
      return;
    }
    
    console.log('🎵 Traitement de l\'audio enregistré...');
    
    // Créer le blob audio
    this.recordedAudioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
    const recordingDuration = Date.now() - this.recordingStartTime;
    
    console.log(`Durée d'enregistrement: ${recordingDuration}ms`);
    console.log(`Taille du fichier: ${(this.recordedAudioBlob.size / 1024).toFixed(2)} KB`);
    
    // Vérifier la durée minimum (500ms)
    if (recordingDuration < 500) {
      console.warn('Enregistrement trop court, ignoré');
      this.recordedAudioBlob = null;
      return;
    }
    
    // Créer l'URL pour la prévisualisation
    this.previewAudioUrl = URL.createObjectURL(this.recordedAudioBlob);
    
    // Calculer la durée pour l'affichage
    const minutes = Math.floor(recordingDuration / 60000);
    const seconds = Math.floor((recordingDuration % 60000) / 1000);
    this.previewDurationText = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    
    // Générer une waveform statique pour la prévisualisation
    this.generatePreviewWaveform();
  }

  private generatePreviewWaveform(): void {
    // Générer une waveform statique basée sur la durée
    const barCount = 30;
    this.previewWaveformBars = Array.from({ length: barCount }, () => 
      Math.random() * 16 + 4 // Entre 4 et 20px
    );
  }

  togglePreviewPlayback(): void {
    if (!this.previewAudioUrl) return;
    
    if (!this.previewAudioElement) {
      this.previewAudioElement = new Audio(this.previewAudioUrl);
      this.previewAudioElement.addEventListener('ended', () => {
        this.isPreviewPlaying = false;
      });
    }
    
    if (this.isPreviewPlaying) {
      this.previewAudioElement.pause();
      this.isPreviewPlaying = false;
    } else {
      this.previewAudioElement.play();
      this.isPreviewPlaying = true;
    }
  }

  deleteRecordedAudio(): void {
    console.log('🗑️ Suppression de l\'enregistrement audio');
    
    // Arrêter la lecture si elle est en cours
    if (this.previewAudioElement) {
      this.previewAudioElement.pause();
      this.previewAudioElement = null;
    }
    
    // Libérer l'URL
    if (this.previewAudioUrl) {
      URL.revokeObjectURL(this.previewAudioUrl);
    }
    
    // Réinitialiser les propriétés
    this.recordedAudioBlob = null;
    this.previewAudioUrl = '';
    this.isPreviewPlaying = false;
    this.previewDurationText = '00:00';
    this.previewWaveformBars = [];
  }

  private async sendRecordedAudio(): Promise<void> {
    if (!this.recordedAudioBlob) return;
    
    try {
      // Upload vers Firebase Storage
      const audioUrl = await this.uploadAudioToFirebase(this.recordedAudioBlob);
      
      if (audioUrl) {
        // Envoyer le message audio
        await this.sendAudioMessage(audioUrl);
        console.log('✅ Message audio envoyé avec succès');
        
        // Nettoyer l'interface
        this.deleteRecordedAudio();
      }
      
    } catch (error) {
      console.error('Erreur lors du traitement audio:', error);
      alert('Erreur lors de l\'envoi du message vocal');
    }
  }

  private async uploadAudioToFirebase(audioBlob: Blob): Promise<string | null> {
    try {
      console.log('📤 Upload audio vers Firebase Storage...');
      
      // Créer un nom de fichier unique
      const fileName = `audio_${this.currentUserId}_${Date.now()}.webm`;
      const filePath = ["ChatWithAi_audios", fileName];
      
      console.log(`Fichier à uploader: ${fileName}`);
      console.log(`Taille: ${(audioBlob.size / 1024).toFixed(2)} KB`);
      
      return new Promise((resolve, reject) => {
        this.storage.upload({
          uploadName: "Upload Audio Message",
          path: filePath,
          data: {
            data: audioBlob,
            metadata: { contentType: 'audio/webm' }
          },
          onComplete: (downloadUrl) => {
            console.log('✅ Audio uploadé avec succès:', downloadUrl);
            resolve(downloadUrl);
          },
          onFail: (error) => {
            console.error('❌ Erreur upload Firebase:', error);
            reject(error);
          }
        });
      });
      
    } catch (error) {
      console.error('Erreur upload Firebase:', error);
      return null;
    }
  }

  private async sendAudioMessage(audioUrl: string): Promise<void> {
    console.log('📤 Envoi du message audio...');
    console.log('URL audio à envoyer:', audioUrl);
    
    // Ajouter le message utilisateur avec audio
    const userMessage: AIMessage = {
      id: this.generateMessageId(),
      text: '', // Pas de texte pour un message audio
      isUser: true,
      timestamp: new Date(),
      audioUrl: audioUrl
    };
    
    console.log('Message utilisateur créé:', userMessage);
    this.messages.push(userMessage);
    
    // Sauvegarder le message audio dans Firebase
    await this.saveMessageToFirebase('', true, audioUrl);
    
    // Forcer le scroll vers le bas
    this.isNearBottom = true;
    this.isUserScrolling = false;
    this.shouldScrollToBottom = true;
    
    // DÉSACTIVÉ : Génération automatique de réponse IA
    // setTimeout(() => {
    //   this.generateAIResponse('Message vocal reçu');
    // }, 1000);
  }
}
